import { RegistryDatabase, MktuClass } from '@/services/Api/generated'
import { Validator } from '@/services/Validator'
import { Yup } from '@/services/Validator/utils/rules'
import { hasArrayChanged } from '@/utils/array'
import { makeAutoObservable } from 'mobx'

export class FiltersStore {
  registrationNumber: string = ''
  applicationNumber: string = ''
  owner: string = ''
  name: string = ''
  databases: RegistryDatabase[] = []
  mktuClasses: MktuClass['classNumber'][] = []

  isChangedFilter: boolean = false

  hideNonActive: boolean = true

  validator = new Validator(
    Yup.object()
      .shape({
        registrationNumber: Yup.string(),
        applicationNumber: Yup.string(),
        owner: Yup.string(),
        name: Yup.string(),
      })
      .test(
        'at-least-one-required',
        'AT_LEAST_ONE_FIELD_REQUIRED',
        (value) =>
          value.registrationNumber.length !== 0 ||
          value.applicationNumber.length !== 0 ||
          value.owner.length !== 0 ||
          value.name.length !== 0
      )
  )

  get isFilled() {
    return (
      this.name.length !== 0 ||
      this.owner.length !== 0 ||
      this.registrationNumber.length !== 0 ||
      this.applicationNumber.length !== 0
    )
  }

  get isDatabaseChanged() {
    return hasArrayChanged(this.databases, this._defaultDatabases)
  }

  get isEmptyAdditionalFilters() {
    return (
      this.mktuClasses.length === 0 &&
      this.registrationNumber.trim().length === 0 &&
      this.applicationNumber.trim().length === 0 &&
      this.owner.trim().length === 0
    )
  }

  get errorText() {
    return this.validator.isValid ? '' : 'Поле обязательно для заполнения'
  }

  get registrationNumberDisabled() {
    return (
      this.name.length !== 0 ||
      this.applicationNumber.length !== 0 ||
      this.owner.length !== 0 ||
      this.mktuClasses.length !== 0
    )
  }

  get applicationNumberDisabled() {
    return (
      this.name.length !== 0 ||
      this.registrationNumber.length !== 0 ||
      this.owner.length !== 0 ||
      this.mktuClasses.length !== 0
    )
  }

  get ownerDisabled() {
    return (
      this.registrationNumber.length !== 0 ||
      this.applicationNumber.length !== 0
    )
  }

  get mktuClassesDisabled() {
    return (
      this.registrationNumber.length !== 0 ||
      this.applicationNumber.length !== 0
    )
  }

  constructor(private _defaultDatabases: RegistryDatabase[] = []) {
    this.databases = [...this._defaultDatabases]
    makeAutoObservable(this, {}, { autoBind: true })
  }

  setRegistrationNumber(numberRegistry: string) {
    this.registrationNumber = numberRegistry
    this.setIsChangedFilter(true)
  }

  setApplicationNumber(numberApplication: string) {
    this.applicationNumber = numberApplication
    this.setIsChangedFilter(true)
  }

  setOwner(owner: string) {
    this.owner = owner
    this.setIsChangedFilter(true)
  }

  setName(value: string) {
    this.name = value
    this.setIsChangedFilter(true)
  }

  setMktuClasses(mktuClassNumbers: MktuClass['classNumber'][]) {
    this.mktuClasses = mktuClassNumbers
    this.setIsChangedFilter(true)
  }

  selectMktuClass(mktuClassNumber: MktuClass['classNumber']) {
    if (this.mktuClasses.includes(mktuClassNumber)) {
      this.mktuClasses = this.mktuClasses.filter((m) => m !== mktuClassNumber)
    } else {
      this.mktuClasses = [...this.mktuClasses, mktuClassNumber]
    }
    this.setIsChangedFilter(true)
  }

  setIsChangedFilter(value: boolean) {
    this.isChangedFilter = value
    if (!this.validator.isValid) {
      this.validate()
    }
  }

  setHideNonActive(value: boolean) {
    this.hideNonActive = value
  }

  setDatabase(database: RegistryDatabase[]) {
    this.databases = database
  }

  clearAdditionalFilters() {
    this.mktuClasses = []
    this.registrationNumber = ''
    this.applicationNumber = ''
    this.owner = ''

    this.setIsChangedFilter(true)
  }

  reset() {
    this.databases = [...this._defaultDatabases]
    this.registrationNumber = ''
    this.applicationNumber = ''
    this.owner = ''
    this.name = ''
    this.mktuClasses = []
  }

  validate() {
    this.validator.validateAll({
      registrationNumber: this.registrationNumber,
      applicationNumber: this.applicationNumber,
      owner: this.owner,
      name: this.name,
    })
    return this.validator.isValid
  }
}
